import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Dialog, DialogContent, DialogActions, Box, Tabs, Tab, IconButton, Typography,
  Button, TextField, Autocomplete, Backdrop, CircularProgress,
  Table, TableHead, TableBody, TableRow, TableCell, Paper, Alert, Chip,
} from "@mui/material";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import ConstructionIcon from "@mui/icons-material/Construction";
import { Formik } from "formik";
import * as Yup from "yup";
import {
  fetchProductsForCompany,
  fetchBranchesForProduct,
  fetchSnFormatsForBranch,
  checkSnAvailability,
  assignSerialNumbers,
  resetSNAvailabilityList,
  resetProductDropdown,
} from "../redux/companies.action";

const tokens = {
  brand: "#3A4D54",
  text: "#1F2A2F",
  textDim: "#5C6D74",
  textMuted: "#98A2B3",
  border: "#E8ECEE",
  surface: "#FFFFFF",
  surfaceAlt: "#F7F9FA",
  successFg: "#067647",
  dangerFg: "#B42318",
  warningFg: "#B54708",
  brandSoft: "rgba(58,77,84,0.06)",
};

const SECTION_LABEL_SX = {
  fontSize: 12,
  fontWeight: 600,
  letterSpacing: "0.02em",
  textTransform: "uppercase",
  color: tokens.textDim,
  mb: 1,
};

const RANGE_TYPE_SEQUENCE = "sequence_number";
const RANGE_TYPE_SERIAL = "serial_number";

function buildContiguousRanges(values) {
  const sorted = [...values].sort((a, b) => a.start - b.start);
  const out = [];
  let curr = null;
  for (const r of sorted) {
    if (!curr) { curr = { ...r }; }
    else if (curr.end + 1 === r.start) { curr.end = r.end; }
    else { out.push(curr); curr = { ...r }; }
  }
  if (curr) out.push(curr);
  return out;
}

export default function CompanyAssignSnDialog({ selectedCompany, onClose, onSuccess }) {
  const [activeTab, setActiveTab] = useState(0);
  const [snack, setSnack] = useState(null);

  if (!selectedCompany) return null;

  return (
    <Dialog
      open
      onClose={onClose}
      scroll="paper"
      fullWidth
      maxWidth="md"
      PaperProps={{ sx: { borderRadius: 2, maxWidth: 1080 } }}
    >
      <Box sx={{
        position: "sticky", top: 0, zIndex: 1, bgcolor: tokens.surface,
        display: "flex", alignItems: "center", justifyContent: "space-between",
        borderBottom: `1px solid ${tokens.border}`, pl: 3, pr: 1.5,
      }}>
        <Box>
          <Typography sx={{ fontSize: 11, color: tokens.textDim, letterSpacing: "0.04em", textTransform: "uppercase", pt: 1.5 }}>
            Serial Number Operations
          </Typography>
          <Typography sx={{ fontSize: 16, fontWeight: 600, color: tokens.text, pb: 1 }}>
            {selectedCompany?.company?.name}
          </Typography>
        </Box>
        <IconButton size="small" onClick={onClose} aria-label="Close">
          <CloseRoundedIcon fontSize="small" />
        </IconButton>
      </Box>
      <Tabs
        value={activeTab}
        onChange={(_, v) => setActiveTab(v)}
        TabIndicatorProps={{ sx: { bgcolor: tokens.brand, height: 2 } }}
        sx={{
          px: 3, borderBottom: `1px solid ${tokens.border}`, minHeight: 40,
          "& .MuiTab-root": { minHeight: 40, fontSize: 13, fontWeight: 500, textTransform: "none", color: tokens.textDim },
          "& .Mui-selected": { color: `${tokens.brand} !important` },
        }}
      >
        <Tab label="Assign" />
        <Tab label="Unassign" />
        <Tab label="Node Reassign" />
      </Tabs>

      <Box hidden={activeTab !== 0}>
        {activeTab === 0 && (
          <AssignTabPanel
            selectedCompany={selectedCompany}
            onClose={onClose}
            onSuccess={(message) => {
              setSnack({ severity: "success", message });
              if (onSuccess) onSuccess();
            }}
            onError={(message) => setSnack({ severity: "error", message })}
          />
        )}
      </Box>
      <Box hidden={activeTab !== 1}>
        {activeTab === 1 && <ComingSoonPanel title="Unassign serial numbers" />}
      </Box>
      <Box hidden={activeTab !== 2}>
        {activeTab === 2 && <ComingSoonPanel title="Reassign nodes" />}
      </Box>

      {snack && (
        <Box sx={{ position: "absolute", bottom: 16, left: 16, right: 16, zIndex: 2 }}>
          <Alert severity={snack.severity} onClose={() => setSnack(null)} variant="filled">
            {snack.message}
          </Alert>
        </Box>
      )}
    </Dialog>
  );
}

function ComingSoonPanel({ title }) {
  return (
    <Box sx={{ py: 10, display: "flex", flexDirection: "column", alignItems: "center", gap: 1.5, color: tokens.textDim }}>
      <ConstructionIcon sx={{ fontSize: 36, color: tokens.textMuted }} />
      <Typography sx={{ fontSize: 14, fontWeight: 500, color: tokens.text }}>{title}</Typography>
      <Typography sx={{ fontSize: 13, color: tokens.textDim, maxWidth: 320, textAlign: "center" }}>
        Available in a follow-up release. Use the Management Portal for now.
      </Typography>
    </Box>
  );
}

function AssignTabPanel({ selectedCompany, onClose, onSuccess, onError }) {
  const dispatch = useDispatch();
  const products = useSelector((s) => s.companies.productDropdown);
  const branches = useSelector((s) => s.companies.branchDropdown);
  const formats = useSelector((s) => s.companies.snFormatDropdown);
  const availability = useSelector((s) => s.companies.snAvailabilityList);
  const isSubmitting = useSelector((s) => s.companies.assignLoading);

  const companyUuid = selectedCompany?.company?.id;

  useEffect(() => {
    if (companyUuid) dispatch(fetchProductsForCompany(companyUuid));
    return () => {
      dispatch(resetProductDropdown());
      dispatch(resetSNAvailabilityList());
    };
  }, [dispatch, companyUuid]);

  const initialValues = {
    product: null,
    branch: null,
    format: null,
    rangeType: RANGE_TYPE_SEQUENCE,
    rangeStart: "",
    rangeEnd: "",
    didPreview: false,
  };

  const validationSchema = Yup.object({
    product: Yup.mixed().required("Product is required"),
    branch: Yup.mixed().required("Branch is required"),
    format: Yup.mixed().required("Serial number pattern is required"),
    rangeStart: Yup.number().typeError("Must be a number").required("Start is required").min(1, "Must be ≥ 1"),
    rangeEnd: Yup.number().typeError("Must be a number").required("End is required")
      .min(Yup.ref("rangeStart"), "End must be ≥ start"),
  });

  const handleSubmit = async (values) => {
    const ranges = buildContiguousRanges([{
      start: Number(values.rangeStart),
      end: Number(values.rangeEnd),
    }]);
    const result = await dispatch(assignSerialNumbers({
      branchUuid: values.branch.node_uuid || values.branch.uuid || values.branch.id,
      productUuid: values.product.product_uuid || values.product.uuid || values.product.id,
      format: values.format.format || values.format,
      ranges,
    }));
    if (result.success) {
      onSuccess("Serial numbers assigned successfully");
      dispatch(resetSNAvailabilityList());
    } else {
      onError(result.message);
    }
  };

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      enableReinitialize
      onSubmit={handleSubmit}
    >
      {(formik) => {
        const onProductChange = async (value) => {
          formik.resetForm();
          formik.setFieldValue("product", value);
          dispatch(resetSNAvailabilityList());
          if (value) {
            await dispatch(fetchBranchesForProduct(value.product_uuid || value.uuid || value.id));
          }
        };
        const onBranchChange = async (value) => {
          formik.setFieldValue("branch", value);
          formik.setFieldValue("format", null);
          formik.setFieldValue("didPreview", false);
          dispatch(resetSNAvailabilityList());
          if (value) {
            await dispatch(fetchSnFormatsForBranch(value.node_uuid || value.uuid || value.id));
          }
        };
        const onFormatChange = (value) => {
          formik.setFieldValue("format", value);
          formik.setFieldValue("didPreview", false);
          dispatch(resetSNAvailabilityList());
        };
        const runPreview = async () => {
          if (!formik.values.format || !formik.values.rangeStart || !formik.values.rangeEnd) return;
          await dispatch(checkSnAvailability({
            branchUuid: formik.values.branch.node_uuid || formik.values.branch.uuid || formik.values.branch.id,
            format: formik.values.format.format || formik.values.format,
            ranges: [{ start: Number(formik.values.rangeStart), end: Number(formik.values.rangeEnd) }],
            isSn: formik.values.rangeType === RANGE_TYPE_SERIAL,
          }));
          formik.setFieldValue("didPreview", true);
        };

        const totalQty = Number(formik.values.rangeEnd || 0) - Number(formik.values.rangeStart || 0) + 1;
        const validRange = totalQty > 0 && Number.isFinite(totalQty);

        return (
          <form onSubmit={formik.handleSubmit}>
            <DialogContent dividers sx={{ bgcolor: tokens.surfaceAlt, p: 3 }}>
              <Box sx={{ display: "grid", gap: 3 }}>
                <Section label="Product">
                  <Autocomplete
                    size="small"
                    options={products}
                    value={formik.values.product}
                    getOptionLabel={(o) => o?.name || o?.product_name || ""}
                    isOptionEqualToValue={(a, b) => (a?.product_uuid || a?.id) === (b?.product_uuid || b?.id)}
                    onChange={(_, v) => onProductChange(v)}
                    renderInput={(params) => (
                      <TextField {...params} placeholder="Select product"
                        error={formik.touched.product && !!formik.errors.product}
                        helperText={formik.touched.product && formik.errors.product}
                      />
                    )}
                  />
                </Section>

                <Section label="Branch" disabled={!formik.values.product}>
                  <Autocomplete
                    size="small"
                    options={branches}
                    value={formik.values.branch}
                    disabled={!formik.values.product}
                    getOptionLabel={(o) => o?.name || o?.node_name || ""}
                    isOptionEqualToValue={(a, b) => (a?.node_uuid || a?.id) === (b?.node_uuid || b?.id)}
                    onChange={(_, v) => onBranchChange(v)}
                    renderInput={(params) => (
                      <TextField {...params} placeholder={formik.values.product ? "Select branch" : "Select a product first"}
                        error={formik.touched.branch && !!formik.errors.branch}
                        helperText={formik.touched.branch && formik.errors.branch}
                      />
                    )}
                  />
                </Section>

                <Section label="Serial Number Pattern" disabled={!formik.values.branch}>
                  <Autocomplete
                    size="small"
                    options={formats}
                    value={formik.values.format}
                    disabled={!formik.values.branch}
                    getOptionLabel={(o) => o?.format || o?.label || ""}
                    isOptionEqualToValue={(a, b) => (a?.format || a) === (b?.format || b)}
                    onChange={(_, v) => onFormatChange(v)}
                    renderInput={(params) => (
                      <TextField {...params} placeholder={formik.values.branch ? "Select pattern" : "Select a branch first"}
                        error={formik.touched.format && !!formik.errors.format}
                        helperText={formik.touched.format && formik.errors.format}
                      />
                    )}
                  />
                </Section>

                <Section label="Range" disabled={!formik.values.format}>
                  <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr auto", gap: 1.5, alignItems: "start" }}>
                    <TextField
                      size="small" label="Start" type="number"
                      value={formik.values.rangeStart}
                      onChange={(e) => { formik.setFieldValue("rangeStart", e.target.value); formik.setFieldValue("didPreview", false); }}
                      onBlur={formik.handleBlur} name="rangeStart"
                      disabled={!formik.values.format}
                      error={formik.touched.rangeStart && !!formik.errors.rangeStart}
                      helperText={formik.touched.rangeStart && formik.errors.rangeStart}
                    />
                    <TextField
                      size="small" label="End" type="number"
                      value={formik.values.rangeEnd}
                      onChange={(e) => { formik.setFieldValue("rangeEnd", e.target.value); formik.setFieldValue("didPreview", false); }}
                      onBlur={formik.handleBlur} name="rangeEnd"
                      disabled={!formik.values.format}
                      error={formik.touched.rangeEnd && !!formik.errors.rangeEnd}
                      helperText={formik.touched.rangeEnd && formik.errors.rangeEnd}
                    />
                    <Button
                      variant="outlined"
                      onClick={runPreview}
                      disabled={!formik.values.format || !validRange}
                      sx={{
                        height: 40, textTransform: "none", borderColor: tokens.brand, color: tokens.brand,
                        "&:hover": { borderColor: tokens.brand, bgcolor: tokens.brandSoft },
                      }}
                    >
                      Check availability
                    </Button>
                  </Box>
                  {validRange && (
                    <Typography sx={{ mt: 1, fontSize: 12, color: tokens.textDim, fontVariantNumeric: "tabular-nums" }}>
                      Quantity: {totalQty.toLocaleString()}
                    </Typography>
                  )}
                </Section>

                {formik.values.didPreview && (
                  <Section label="Preview">
                    <AvailabilityTable rows={availability.data} exceedsRange={availability.isSNexceedRange} />
                  </Section>
                )}
              </Box>
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 2, borderTop: `1px solid ${tokens.border}` }}>
              <Box sx={{ flex: 1 }} />
              <Button onClick={onClose} disabled={isSubmitting} sx={{ textTransform: "none", color: tokens.textDim }}>
                Cancel
              </Button>
              <Button
                type="submit" variant="contained" disableElevation
                disabled={isSubmitting || !formik.isValid || !formik.dirty}
                sx={{
                  textTransform: "none", bgcolor: tokens.brand,
                  "&:hover": { bgcolor: "#2C3B41" },
                }}
              >
                {isSubmitting ? "Assigning…" : "Assign"}
              </Button>
            </DialogActions>
            <Backdrop open={isSubmitting} sx={{ zIndex: 1500, color: "#fff", flexDirection: "column", gap: 1.5 }}>
              <CircularProgress color="inherit" size={32} />
              <Typography sx={{ fontSize: 13 }}>Assigning serial numbers…</Typography>
            </Backdrop>
          </form>
        );
      }}
    </Formik>
  );
}

function Section({ label, disabled, children }) {
  return (
    <Box sx={{ opacity: disabled ? 0.45 : 1, transition: "opacity 200ms cubic-bezier(0.22,1,0.36,1)" }}>
      <Typography sx={SECTION_LABEL_SX}>{label}</Typography>
      {children}
    </Box>
  );
}

function AvailabilityTable({ rows, exceedsRange }) {
  if (!rows || rows.length === 0) {
    return (
      <Paper variant="outlined" sx={{ p: 2, borderColor: tokens.border, bgcolor: tokens.surface }}>
        <Typography sx={{ fontSize: 13, color: tokens.textDim }}>
          No conflicts in this range. Submitting will assign all numbers to the selected product.
        </Typography>
      </Paper>
    );
  }
  return (
    <Paper variant="outlined" sx={{ borderColor: tokens.border, bgcolor: tokens.surface, overflow: "hidden" }}>
      {exceedsRange && (
        <Alert severity="warning" sx={{ borderRadius: 0 }}>
          Range exceeds available serial numbers for this branch.
        </Alert>
      )}
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontSize: 12, fontWeight: 600, color: tokens.textDim, textTransform: "uppercase" }}>From</TableCell>
            <TableCell sx={{ fontSize: 12, fontWeight: 600, color: tokens.textDim, textTransform: "uppercase" }}>To</TableCell>
            <TableCell sx={{ fontSize: 12, fontWeight: 600, color: tokens.textDim, textTransform: "uppercase" }}>Currently assigned to</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((r, i) => (
            <TableRow key={i}>
              <TableCell sx={{ fontVariantNumeric: "tabular-nums" }}>{r.from_seq_num ?? r.from}</TableCell>
              <TableCell sx={{ fontVariantNumeric: "tabular-nums" }}>{r.to_seq_num ?? r.to}</TableCell>
              <TableCell>
                {r.product?.name ? (
                  <Chip size="small" label={r.product.name} sx={{ bgcolor: tokens.brandSoft, color: tokens.brand, fontWeight: 500 }} />
                ) : (
                  <Typography sx={{ fontSize: 13, color: tokens.successFg }}>Unassigned</Typography>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Paper>
  );
}
